import React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { ChatActionsMenu, type ChatActionItem } from './ChatActionsMenu';

type Props = {
  /** `null` beim Entwurf und solange die Chat-Metadaten laden — der Header hält dann nur die Höhe. */
  title: string | null;
  /** Optimistisches Update + Rollback/Toast macht der Aufrufer (ChatPage). */
  onRename: (nextTitle: string) => void | Promise<unknown>;
  onDelete: () => void | Promise<unknown>;
  /** Umbenennen/Löschen nur anbieten, wenn es einen persistierten Chat gibt (nicht beim Entwurf). */
  menuEnabled?: boolean;
  isPinned?: boolean;
  onTogglePin?: () => void;
  extraItems?: ChatActionItem[];
  /** z. B. der Gedächtnis-Button — auf der gleichen Ebene wie der Titel, rechtsbündig. */
  rightAccessory?: React.ReactNode;
  /** Platz links, wenn die Chat-Seitenleiste zu ist und der Öffnen-Knopf darüber liegt. */
  leadingInset?: boolean;
};

/** Identisch in Anzeige- und Edit-Zustand, damit beim Umschalten kein Layout-Sprung entsteht.
 *  Bewusst schlank gehalten (analog Claude-UI): kleiner, Gewicht 500 statt fett. */
const TITLE_TEXT_CLASS = 'text-[clamp(0.8125rem,2vw,0.9375rem)] font-medium leading-6';
/** Muss zum Server-Limit passen — `server/routes/ai.ts` kappt Titel bei 120 Zeichen. */
const TITLE_MAX_LENGTH = 120;
/** `leading-6` = 24px; Textarea wächst bis 3 Zeilen (+ Padding/Border), danach scrollt sie. */
const TEXTAREA_MAX_HEIGHT_PX = 24 * 3 + 10;
/** Muss `DEFAULT_AI_CHAT_TITLE` in `server/services/ai/chatTitle.ts` entsprechen. */
const DEFAULT_TITLE = 'Neuer Chat';

export function ChatHeader({
  title,
  onRename,
  onDelete,
  menuEnabled = true,
  isPinned = false,
  onTogglePin,
  extraItems,
  rightAccessory,
  leadingInset = false,
}: Props) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  /** Der Default-Titel ist nur ein Platzhalter, bis die Titel-Generierung nach dem ersten Turn greift. */
  const resolvedTitle = title && title.trim() !== DEFAULT_TITLE ? title : null;
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const skipNextBlurRef = React.useRef(false);

  const autoResize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
    el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    if (!editing) return;
    autoResize();
  }, [editing, draft, autoResize]);

  React.useLayoutEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  const startEditing = () => {
    skipNextBlurRef.current = false;
    setDraft(resolvedTitle ?? '');
    setEditing(true);
  };

  const cancelEditing = () => {
    skipNextBlurRef.current = true;
    setEditing(false);
  };

  const saveDraft = () => {
    setEditing(false);
    const next = draft.trim();
    // Leer/nur Whitespace oder unverändert → alter Titel bleibt, nichts speichern.
    if (!next || next === (resolvedTitle ?? '').trim()) return;
    void onRename(next);
  };

  return (
    <header className={`sticky top-0 z-30 flex min-h-10 shrink-0 items-center gap-1 bg-transparent px-3 py-1 md:min-h-11 md:px-4 ${leadingInset ? 'md:pl-14' : ''}`}>
      <div
        id="chat-copy-toast-host"
        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
      />
      {editing ? (
        <div className="flex w-full min-w-0 items-start gap-1.5">
          <textarea
            ref={textareaRef}
            value={draft}
            rows={1}
            maxLength={TITLE_MAX_LENGTH}
            aria-label="Chat-Titel bearbeiten"
            onChange={(event) => setDraft(event.target.value.replace(/[\r\n]+/g, ' '))}
            onBlur={() => {
              if (skipNextBlurRef.current) {
                skipNextBlurRef.current = false;
                return;
              }
              saveDraft();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                // Shift+Enter wird ignoriert — Titel hat keine Zeilenumbrüche.
                if (!event.shiftKey) event.currentTarget.blur();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                cancelEditing();
              }
            }}
            className={`w-full min-w-0 resize-none rounded-md border border-app-border bg-app-surface px-2 py-1 text-app-text outline-none transition-colors focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue ${TITLE_TEXT_CLASS}`}
          />
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              skipNextBlurRef.current = true;
              saveDraft();
            }}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-pipedrive-blue text-white hover:bg-blue-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-pipedrive-blue"
            aria-label="Titel speichern"
            title="Speichern"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={cancelEditing}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-app-border bg-app-surface text-app-text-subtle hover:bg-app-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-pipedrive-blue"
            aria-label="Bearbeiten abbrechen"
            title="Abbrechen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <h2
            title={resolvedTitle ?? undefined}
            className={`min-w-0 max-w-[70%] truncate text-app-text max-[480px]:whitespace-normal max-[480px]:line-clamp-2 ${TITLE_TEXT_CLASS}`}
          >
            {resolvedTitle}
          </h2>
          {menuEnabled && (resolvedTitle || (extraItems && extraItems.length > 0)) ? (
            <ChatActionsMenu
              onRename={startEditing}
              onDelete={() => void onDelete()}
              isPinned={isPinned}
              onTogglePin={onTogglePin}
              extraItems={extraItems}
              triggerClassName="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200/70 hover:text-app-text-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-pipedrive-blue"
              renderTrigger={(open) => (
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              )}
            />
          ) : null}
        </>
      )}
      {!editing && rightAccessory ? (
        <div className="ml-auto flex shrink-0 items-center gap-1 pl-2">{rightAccessory}</div>
      ) : null}
    </header>
  );
}
